import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { useSignIn, useClerk } from "@clerk/clerk-react";
import Logo from "../../components/topBar/logo.svg";

const ICONS = {
  email: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </svg>
  ),
  password: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  ),
  code: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  ),
};

function FloatField({ id, label, type = "text", autoComplete, name, inputMode, value, onChange, required, icon }) {
  return (
    <div className="relative">
      <input
        id={id}
        type={type}
        name={name}
        autoComplete={autoComplete}
        inputMode={inputMode}
        placeholder=" "
        className="peer w-full rounded-3xl border-2 border-mainDark/20 bg-mainWhite pl-12 pr-5 pb-3 pt-7 text-mainDark shadow-result outline-none transition-all duration-200 focus:border-mainBlue focus:shadow-search"
        value={value}
        onChange={onChange}
        required={required}
      />
      {/* Icon sits after input in DOM so peer-focus works */}
      {icon && (
        <span className="pointer-events-none absolute left-4 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-mainDark/25 transition-colors duration-200 peer-focus:text-mainBlue peer-[:not(:placeholder-shown)]:text-mainBlue/50">
          {ICONS[icon]}
        </span>
      )}
      <label
        htmlFor={id}
        className="pointer-events-none absolute left-12 top-2.5 text-[10px] font-medium uppercase tracking-widest text-mainBlue transition-all duration-200 peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-sm peer-placeholder-shown:normal-case peer-placeholder-shown:tracking-normal peer-placeholder-shown:font-light peer-placeholder-shown:text-mainDark/40 peer-focus:top-2.5 peer-focus:translate-y-0 peer-focus:text-[10px] peer-focus:uppercase peer-focus:tracking-widest peer-focus:font-medium peer-focus:text-mainBlue"
      >
        {label}
      </label>
    </div>
  );
}

function ArrowLeft({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M19 12H5M12 5l-7 7 7 7" />
    </svg>
  );
}

function ArrowRight({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12h14M12 5l7 7-7 7" />
    </svg>
  );
}

function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isLoaded, signIn } = useSignIn();
  const { setActive } = useClerk();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  /** Forgot password: 0 off, 1 code sent, 2 entering code + new password */
  const [resetStep, setResetStep] = useState(0);
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");

  const clerkError = (err) =>
    err?.errors?.[0]?.message || err?.message || t("login.genericError");

  const afterSignInNavigate = () => navigate("/");

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError("");
    if (!isLoaded || !signIn) return;
    setLoading(true);
    try {
      const result = await signIn.create({ identifier: email.trim(), password });
      if (result.status === "complete" && result.createdSessionId) {
        await setActive({ session: result.createdSessionId });
        afterSignInNavigate();
      } else {
        setError(t("login.additionalSteps"));
      }
    } catch (err) {
      setError(clerkError(err));
    } finally {
      setLoading(false);
    }
  };

  const startReset = async (e) => {
    e.preventDefault();
    setError("");
    if (!isLoaded || !signIn) return;
    if (!email.trim()) { setError(t("login.enterEmailFirst")); return; }
    setLoading(true);
    try {
      await signIn.create({ strategy: "reset_password_email_code", identifier: email.trim() });
      setResetStep(1);
    } catch (err) {
      setError(clerkError(err));
    } finally {
      setLoading(false);
    }
  };

  const completeReset = async (e) => {
    e.preventDefault();
    setError("");
    if (!isLoaded || !signIn) return;
    setLoading(true);
    try {
      const result = await signIn.attemptFirstFactor({
        strategy: "reset_password_email_code",
        code: resetCode.trim(),
        password: newPassword,
      });
      if (result.status === "complete" && result.createdSessionId) {
        await setActive({ session: result.createdSessionId });
        setResetStep(0); setResetCode(""); setNewPassword("");
        afterSignInNavigate();
      } else {
        setError(t("login.resetFailed"));
      }
    } catch (err) {
      setError(clerkError(err));
    } finally {
      setLoading(false);
    }
  };

  if (!isLoaded) return null;

  return (
    <div className="min-h-screen flex flex-col md:flex-row">

      {/* ── Brand panel (desktop only) ── */}
      <div className="relative hidden md:flex md:w-[44%] bg-primary flex-col items-center justify-center p-14 overflow-hidden">
        {/* Decorative blobs */}
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-mainBlue/10" />
        <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-mainAccent/10" />
        <div className="absolute top-1/2 -left-10 w-40 h-40 rounded-full bg-mainBlue/15" />

        <Link to="/" className="relative z-10 flex flex-col items-center gap-6 text-center">
          <img src={Logo} alt={t("topBar.logoAlt")} className="max-w-[160px] brightness-0 invert" />
          <div className="w-20 h-px bg-onPrimary/25" />
          <p className="text-onPrimary/70 text-xs uppercase tracking-[0.25em] font-light max-w-[200px]">
            {t("topBar.tagline")}
          </p>
        </Link>
      </div>

      {/* ── Form panel ── */}
      <div className="flex-1 flex flex-col">

        {/* Mobile logo */}
        <div className="md:hidden px-6 pt-7">
          <Link to="/">
            <img src={Logo} alt={t("topBar.logoAlt")} className="max-h-[16px]" />
          </Link>
        </div>

        <div className="flex-1 flex items-center justify-center px-6 py-14">
          <div className="w-full max-w-sm">

            {/* Back to homepage */}
            <Link
              to="/"
              className="group mb-8 -ml-1 inline-flex items-center gap-2 text-sm font-light text-mainDark/40 transition-colors duration-200 hover:text-mainDark"
            >
              <ArrowLeft className="h-4 w-4 transition-transform duration-200 group-hover:-translate-x-1" />
              {t("login.backHome")}
            </Link>

            <h1 className="mb-8 text-3xl font-light text-mainDark">
              {resetStep === 0 ? t("login.heading") : t("login.resetHeading")}
            </h1>

            {resetStep === 0 && (
              <form onSubmit={handleSignIn} className="flex flex-col gap-4">
                <FloatField
                  id="email" label={t("login.email")} type="email" icon="email"
                  name="email" autoComplete="email"
                  value={email} onChange={(e) => setEmail(e.target.value)} required
                />
                <FloatField
                  id="password" label={t("login.password")} type="password" icon="password"
                  name="password" autoComplete="current-password"
                  value={password} onChange={(e) => setPassword(e.target.value)} required
                />
                {error && <p className="text-sm font-light text-red-500 px-1">{error}</p>}
                <button
                  type="submit" disabled={loading}
                  className="group mt-2 w-full rounded-3xl bg-primary py-4 text-onPrimary shadow-search transition-all duration-200 hover:brightness-110 hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(0,0,0,0.25)] active:translate-y-0 active:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:translate-y-0 disabled:hover:shadow-search disabled:hover:brightness-100"
                >
                  {loading ? t("login.pleaseWait") : t("login.signInSubmit")}
                </button>
                <button
                  type="button" onClick={startReset}
                  className="group flex w-full items-center justify-center gap-1.5 text-sm font-light text-mainDark/40 transition-colors duration-200 hover:text-mainBlue"
                >
                  {t("login.forgotPassword")}
                  <ArrowRight className="h-3.5 w-3.5 opacity-0 -translate-x-1 transition-all duration-200 group-hover:opacity-100 group-hover:translate-x-0" />
                </button>
              </form>
            )}

            {resetStep === 1 && (
              <form onSubmit={completeReset} className="flex flex-col gap-4">
                <p className="text-sm font-light text-mainDark/60 mb-1">
                  <Trans
                    i18nKey="login.resetIntro"
                    values={{ email }}
                    components={{ strong: <strong className="font-semibold text-mainDark" /> }}
                  />
                </p>
                <FloatField
                  id="resetCode" label={t("login.verificationCode")} icon="code"
                  autoComplete="one-time-code" inputMode="numeric"
                  value={resetCode} onChange={(e) => setResetCode(e.target.value)} required
                />
                <FloatField
                  id="newPassword" label={t("login.newPassword")} type="password" icon="password"
                  autoComplete="new-password"
                  value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required
                />
                {error && <p className="text-sm font-light text-red-500 px-1">{error}</p>}
                <button
                  type="submit" disabled={loading}
                  className="mt-2 w-full rounded-3xl bg-primary py-4 text-onPrimary shadow-search transition-all duration-200 hover:brightness-110 hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(0,0,0,0.25)] active:translate-y-0 active:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:translate-y-0 disabled:hover:shadow-search disabled:hover:brightness-100"
                >
                  {loading ? t("login.pleaseWait") : t("login.updatePassword")}
                </button>
                <button
                  type="button"
                  onClick={() => { setResetStep(0); setError(""); }}
                  className="group flex items-center gap-1.5 text-sm font-light text-mainDark/40 transition-colors duration-200 hover:text-mainDark"
                >
                  <ArrowLeft className="h-3.5 w-3.5 transition-transform duration-200 group-hover:-translate-x-1" />
                  {t("login.backToSignIn")}
                </button>
              </form>
            )}

            <p className="mt-10 text-sm font-light text-mainDark/50">
              {t("login.noAccount")}{" "}
              <Link
                to="/signup"
                className="group inline-flex items-center gap-1 text-mainBlue underline underline-offset-2 transition-colors duration-200 hover:text-mainDark"
              >
                {t("login.createOne")}
                <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
              </Link>
            </p>

          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
